import React, { useEffect, useState } from 'react';
import { FaInstagram, FaFacebook, FaLinkedin, FaYoutube } from 'react-icons/fa';
import './App.css';

type Offer = {
  name: string;
  description: string;
  highlights: string[];
};

type Partner = {
  name: string;
  logo: string;
};

type BlogPost = {
  title: string;
  summary: string;
  url: string;
};

type BlogApiPost = {
  title: string;
  summary: string;
  url: string;
  published_at: string;
};

const SUPPORT_EMAIL = 'support@pythonicit.com';
const BLOG_API_URL = process.env.REACT_APP_BLOG_API_URL || 'https://blog.pythonicit.com/api/articles/latest';

const offers: Offer[] = [
  {
    name: 'Launch',
    description: 'For teams that need a reliable IT baseline and fast response.',
    highlights: [
      'Endpoint and cloud monitoring',
      'SaaS and domain administration',
      'Business-hours support desk'
    ]
  },
  {
    name: 'Scale',
    description: 'For growing operations that need stronger security and automation.',
    highlights: [
      'Everything in Launch',
      'Workflow automation and integrations',
      'Advanced threat prevention'
    ]
  },
  {
    name: 'Command',
    description: 'For organizations that need always-on reliability and strategic guidance.',
    highlights: [
      'Everything in Scale',
      '24/7 incident response',
      'Dedicated technical advisor'
    ]
  }
];

const services: string[] = [
  'Managed IT operations',
  'Cloud and infrastructure engineering',
  'Security hardening and monitoring',
  'Automation-first support workflows'
];

const partners: Partner[] = [
  { name: 'AWS', logo: '/images/aws_logo.png' },
  { name: 'CompTIA', logo: '/images/comptia_logo.png' },
  { name: 'Dell', logo: '/images/dell_logo.png' },
  { name: 'Docker', logo: '/images/docker_logo.png' },
  { name: 'Fly.io', logo: '/images/fly_logo.png' },
  { name: 'Google', logo: '/images/google_logo.png' },
  { name: 'Microsoft', logo: '/images/microsoft_logo.png' }
];

const socialLinks = [
  { name: 'Instagram', href: 'https://www.instagram.com/pythonicit', icon: FaInstagram },
  { name: 'Facebook', href: 'https://www.facebook.com/pythonicit', icon: FaFacebook },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/pythonicit', icon: FaLinkedin },
  { name: 'YouTube', href: 'https://www.youtube.com/@pythonicit', icon: FaYoutube }
];

const App: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isBlogLoading, setIsBlogLoading] = useState<boolean>(true);
  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=Pricing%20and%20Services%20Inquiry`;

  useEffect(() => {
    const controller = new AbortController();

    const fetchBlogPosts = async (): Promise<void> => {
      try {
        const response = await fetch(BLOG_API_URL, { signal: controller.signal });
        if (!response.ok) {
          return;
        }

        const data: BlogApiPost[] = await response.json();
        if (!Array.isArray(data)) {
          return;
        }

        const latest = data.slice(0, 3).map((post) => ({
          title: post.title,
          summary: post.summary,
          url: post.url
        }));

        setBlogPosts(latest);
      } catch (error) {
        // Keep section visible even if API is temporarily unavailable.
        setBlogPosts([]);
      } finally {
        setIsBlogLoading(false);
      }
    };

    fetchBlogPosts();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <div className="site-shell">
      <div className="ambient ambient-left" aria-hidden="true" />
      <div className="ambient ambient-right" aria-hidden="true" />

      <nav className="top-nav">
        <a href="#home">Home</a>
        <a href="#about">About</a>
        <a href="#services">Services</a>
        <a href="#plans">Plans/Pricing</a>
        <a href="#partners">Partners</a>
        <a href="#blog">Blog</a>
      </nav>

      <header className="hero" id="home">
        <img
          src="images/pythonicitlogo.png"
          alt="PythonicIT Logo"
          className="company-logo"
          style={{ height: 120 }}
        />
        <h1>PythonicIT</h1>
        <p className="hero-copy">
          Empowering Your Business with Automation, AI, Security, and IT Support.
          We design resilient systems, automate the repetitive work, and keep your business secure while you scale.
        </p>
        <div className="hero-actions">
          <a className="btn btn-primary" href={mailtoHref}>
            Contact Us
          </a>
          <a className="btn btn-secondary" href="#plans">
            Explore Plans
          </a>
        </div>
      </header>

      <main>
        <section className="panel" id="about">
          <h2>About</h2>
          <p className="section-copy centered-copy">
            PythonicIT is built for teams that need modern IT support with engineering depth. We combine operational
            reliability, security-first thinking, and automation to help businesses move faster with less friction.
          </p>
        </section>

        <section className="panel" id="services">
          <h2>What we handle</h2>
          <div className="service-grid">
            {services.map((service: string) => (
              <article className="service-card" key={service}>
                <span className="dot" aria-hidden="true" />
                <p>{service}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="panel" id="plans">
          <h2>Plans built around outcomes, not fixed numbers</h2>
          <p className="section-copy">
            Every environment is different. We scope your plan to your stack, risk profile, and response targets.
          </p>

          <div className="plan-grid">
            {offers.map((offer: Offer) => (
              <article className="plan-card" key={offer.name}>
                <h3>{offer.name}</h3>
                <p>{offer.description}</p>
                <ul>
                  {offer.highlights.map((point: string) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <a href={mailtoHref}>Contact us for pricing</a>
              </article>
            ))}
          </div>
        </section>

        <section className="panel" id="partners">
          <h2>Partners</h2>
          <p className="section-copy centered-copy">
            We collaborate with trusted platform and technology partners to deliver dependable solutions.
          </p>
          <div className="partner-grid">
            {partners.map((partner: Partner) => (
              <article className="partner-card" key={partner.name}>
                <img src={partner.logo} alt={`${partner.name} logo`} />
              </article>
            ))}
          </div>
        </section>

        <section className="panel" id="blog">
          <h2>Blog</h2>
          <div className="blog-grid">
            {blogPosts.length > 0 ? (
              blogPosts.map((post: BlogPost) => (
                <a className="blog-card" key={post.url} href={post.url} target="_blank" rel="noreferrer">
                  <h3>{post.title}</h3>
                  <p>{post.summary}</p>
                </a>
              ))
            ) : (
              <p className="section-copy centered-copy">
                {isBlogLoading ? 'Loading latest posts...' : 'Latest posts are temporarily unavailable here.'}{' '}
                Browse all posts at{' '}
                <a href="https://blog.pythonicit.com" target="_blank" rel="noreferrer">
                  blog.pythonicit.com
                </a>
                .
              </p>
            )}
          </div>
        </section>

        <section className="panel cta-panel" id="contact">
          <h2>Need a custom quote?</h2>
          <p>
            Send your requirements to <a href={mailtoHref}>{SUPPORT_EMAIL}</a> and we will propose the right support model.
          </p>
        </section>
      </main>

      <footer className="site-footer">
        <ul className="social-links">
          {socialLinks.map((link) => {
            const Icon = link.icon;
            return (
              <li key={link.name}>
                <a href={link.href} target="_blank" rel="noreferrer" aria-label={link.name}>
                  <Icon />
                </a>
              </li>
            );
          })}
        </ul>
        <p className="site-credit" aria-label={`Founded 2011 to ${new Date().getFullYear()}`}>
          Founded 2011 - {new Date().getFullYear()}
        </p>
        <a className="go-top" title="Back to Top" href="#home">
          Back to Top
        </a>
      </footer>
    </div>
  );
};

export default App;